import React, { useState } from 'react';
import { Trans } from 'react-i18next';
import { Lock, GitBranch, Building2, Shield, User, ExternalLink, Info } from 'lucide-react';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import SearchableSelect from '../ui/SearchableSelect';
import { providerGlyph } from './ProviderIcon';
import { openExternal } from '../../utils/openExternal';
import { APP_NAME } from '../../utils/appInfo';
import { filterByOwner } from '../../services/git/repoCatalog';
import './RepoPicker.css';

/**
 * The pieces "Clone a repository" (WorkspaceModal) and "Link a code
 * repository" (projects/LinkRepoModal) share (26.10.2026, owner: the link
 * picker should work like the clone modal): which connected account, which
 * repository of it, and — for linking — which branch. Each piece is a thin
 * wrapper over the app's own pickers (CompactSelectPicker, SearchableSelect),
 * so both dialogs look and behave the same.
 */

/** "Clone from" / "Account": the connected git accounts, as the app's dropdown. */
export function GitAccountSelect({ sources, value, onChange, disabled, label }) {
  return (
    <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
      <CompactSelectPicker
        value={value}
        onChange={onChange}
        options={sources.map((s) => ({ value: s.key, label: s.label }))}
        disabled={disabled}
        aria-label={label}
      />
    </div>
  );
}

/**
 * An owner's picture (a user or an organisation). A picture that does not
 * load — a GitLab Gravatar outside the CSP — falls back to an icon.
 */
export function OwnerAvatar({ url, type = 'org', size = 14 }) {
  const [failed, setFailed] = useState(null);
  if (!url || failed === url) {
    const Icon = type === 'user' ? User : Building2;
    return (
      <span className="repo-owner-avatar repo-owner-avatar--icon" style={{ width: size, height: size }} aria-hidden>
        <Icon size={Math.max(10, size - 4)} />
      </span>
    );
  }
  return (
    <img
      className={`repo-owner-avatar${type === 'org' ? ' repo-owner-avatar--org' : ''}`}
      src={url}
      alt=""
      width={size}
      height={size}
      onError={() => setFailed(url)}
    />
  );
}

/**
 * "Owner": All · <you> · <each organisation> (28.10.2026, like GitHub
 * Desktop's owner groups). Shown only when there is more than one owner.
 */
export function RepoOwnerSelect({ owners, value, onChange, allText, label, disabled }) {
  if (!owners || owners.length < 2) return null;
  const options = [
    { value: 'all', label: allText },
    ...owners.map((o) => ({
      value: o.key,
      label: o.login,
      leading: <OwnerAvatar url={o.avatarUrl} type={o.type} size={16} />,
    })),
  ];
  return (
    <div className="modal-picker repo-owner-select" style={{ display: 'flex', width: '100%' }} data-testid="repo-owner-select">
      <CompactSelectPicker value={value || 'all'} onChange={onChange} options={options} disabled={disabled} aria-label={label} />
    </div>
  );
}

/** Repositories of the chosen owner; a chosen repository of another owner stays listed (and so stays shown). */
export function reposForOwner(repos, ownerKey, selectedUrl) {
  const list = filterByOwner(repos, ownerKey);
  if (!selectedUrl || list.some((r) => r.url === selectedUrl)) return list;
  const picked = (repos || []).find((r) => r.url === selectedUrl);
  return picked ? [...list, picked] : list;
}

const NOTICE_KEYS = {
  oauthRestricted: 'oauthRestricted',
  sso: 'sso',
  missingScope: 'missingScope',
  fineGrained: 'fineGrained',
  orgUnavailable: 'orgUnavailable',
};

/**
 * Why an organisation's repositories are missing (repoCatalog.js notices):
 * the OAuth app is not approved, SAML SSO, a missing scope, a fine-grained
 * token. With an owner chosen, only that owner's notices (and general ones).
 */
export function RepoAccessNotices({ notices, ownerKey = 'all', t, providerName = 'GitHub' }) {
  const shown = (notices || []).filter((n) => NOTICE_KEYS[n.kind] && (ownerKey === 'all' || !n.owner || n.owner.toLowerCase() === String(ownerKey).toLowerCase()));
  if (!shown.length) return null;
  return (
    <div className="repo-notices" data-testid="repo-access-notices">
      {shown.map((n, i) => {
        const base = `projects.repo.access.${NOTICE_KEYS[n.kind]}`;
        const variant = n.kind === 'missingScope' ? `${base}.${n.tokenKind === 'classic' ? 'classic' : n.tokenKind === 'gitea' ? 'gitea' : 'oauth'}` : n.kind === 'fineGrained' ? `${base}.${n.owner ? 'owner' : 'general'}` : base;
        const values = { app: APP_NAME, owner: n.owner, scope: n.scope || '', provider: providerName };
        return (
          <div key={`${n.kind}:${n.owner}:${i}`} className="repo-notice" role="note" data-testid={`repo-notice-${n.kind}`} data-owner={n.owner || ''}>
            <Info size={14} className="repo-notice__icon" aria-hidden />
            <div className="repo-notice__body">
              <p className="repo-notice__text">
                <Trans i18nKey={`${variant}.text`} values={values} components={{ b: <strong /> }} />
              </p>
              {(n.url || n.secondaryUrl) && (
                <div className="repo-notice__actions">
                  {n.url && (
                    <button type="button" className="gd-btn repo-notice__btn" onClick={() => openExternal(n.url)} data-testid="repo-notice-action">
                      <ExternalLink size={12} aria-hidden /> {t(`${variant}.action`, values)}
                    </button>
                  )}
                  {n.secondaryUrl && (
                    <button type="button" className="gd-btn repo-notice__btn repo-notice__btn--quiet" onClick={() => openExternal(n.secondaryUrl)} data-testid="repo-notice-secondary">
                      {t(`${variant}.secondary`, values)}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * A searchable repository picker (live filter as you type). Rows show
 * `owner/name` with the owner's picture, a lock for private repositories
 * and — when `login` is given — an organization marker for repositories
 * that belong to someone else.
 */
export function RepoSearchSelect({
  repos,
  value,
  onChange,
  loading,
  error,
  emptyText,
  onEmptyAction,
  onOpen,
  placeholder,
  searchPlaceholder,
  noMatchText,
  selectedText,
  providerId = 'github',
  login = '',
  orgText = '',
  disabled = false,
}) {
  const Glyph = providerGlyph(providerId);
  return (
    <SearchableSelect
      options={repos}
      value={value}
      onChange={onChange}
      getKey={(r) => r.url}
      getLabel={(r) => r.fullName}
      loading={loading}
      error={error}
      emptyText={emptyText}
      noMatchText={noMatchText}
      onEmptyAction={onEmptyAction}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      onOpen={onOpen}
      disabled={disabled}
      leadingIcon={<span className="text-gray-400" style={{ display: 'inline-flex' }}><Glyph size={16} /></span>}
      renderOption={(repo, { selected }) => (
        <>
          {repo.ownerAvatar ? (
            <OwnerAvatar url={repo.ownerAvatar} type={repo.ownerType} size={14} />
          ) : (
            <span className="text-gray-400 flex-shrink-0" style={{ display: 'inline-flex' }}>
              <Glyph size={13} />
            </span>
          )}
          <span className="min-w-0 flex-1 truncate">{repo.fullName}</span>
          {repo.private && (
            <span className="text-gray-400 flex-shrink-0" style={{ display: 'inline-flex' }} data-testid="repo-private-marker">
              <Lock size={12} aria-hidden />
            </span>
          )}
          {login && orgText && repo.owner && repo.owner.toLowerCase() !== login.toLowerCase() && (
            <span className="pj-result-meta flex items-center gap-1 flex-shrink-0" data-testid="repo-org-marker">
              <Building2 size={11} aria-hidden /> {orgText}
            </span>
          )}
          {selected && (
            <span className="text-xs" style={{ color: 'var(--accent, #8869eb)' }}>
              {selectedText}
            </span>
          )}
        </>
      )}
    />
  );
}

/** A searchable branch picker; the repository's default branch is marked. */
export function BranchSearchSelect({
  branches,
  value,
  onChange,
  loading,
  error,
  emptyText,
  noMatchText,
  placeholder,
  searchPlaceholder,
  defaultBranch = '',
  defaultText = '',
  disabled = false,
}) {
  return (
    <SearchableSelect
      options={branches}
      value={value}
      onChange={onChange}
      getKey={(b) => b.name}
      getLabel={(b) => b.name}
      loading={loading}
      error={error}
      emptyText={emptyText}
      noMatchText={noMatchText}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      disabled={disabled}
      leadingIcon={<GitBranch size={16} className="text-gray-400" />}
      renderOption={(b) => (
        <>
          <GitBranch size={13} className="text-gray-400 flex-shrink-0" />
          <span className="min-w-0 flex-1 truncate">{b.name}</span>
          {b.protected && <Shield size={11} className="text-gray-400 flex-shrink-0" aria-hidden />}
          {b.name === defaultBranch && defaultText && <span className="pj-result-meta flex-shrink-0">{defaultText}</span>}
        </>
      )}
    />
  );
}
